import React from 'react';
import {
  Box, Typography, Card, CardContent, Alert, Button,
} from '@mui/material';
import { Security as SecurityIcon, AdminPanelSettings as AdminIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Role definitions and permissions
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  MANAGER: 'manager',
  USER: 'user',
  VIEWER: 'viewer',
};

export const ROLE_PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: ['*'], // All permissions
  [ROLES.ADMIN]: [
    'dashboard',
    'forms',
    'agents',
    'analytics',
    'settings',
    'security',
    'performance',
    'data-sources',
    'rag-dashboard',
    'help',
    // Form-specific permissions
    'forms.create',
    'forms.edit',
    'forms.delete',
    'forms.publish',
    // Agent-specific permissions
    'agents.create',
    'agents.edit',
    'agents.delete',
    'agents.test',
    // Workflow permissions
    'workflows.execute',
    'workflows.manage',
    'workflows.monitor',
    // Security permissions
    'security.manage',
    'security.view_logs',
    'security.manage_users',
    'security.manage_roles',
    'security.manage_permissions',
    // API permissions
    'api.access',
    'api.rate_limit_bypass',
    // Content permissions
    'content.view',
    'content.create',
    'content.edit',
    'content.publish',
    // Analytics permissions
    'analytics.view',
    'analytics.export',
    'analytics.reports',
    // Integration permissions
    'integrations.view',
    'integrations.manage',
    'integrations.configure',
    // MCP permissions
    'mcp.view',
    'mcp.manage',
    'mcp.server',
    'mcp.monitor',
    'mcp.analytics',
    'mcp.tools',
  ],
  [ROLES.MANAGER]: [
    'dashboard',
    'forms',
    'agents',
    'analytics',
    'rag-dashboard',
    'help',
    // Form permissions
    'forms.create',
    'forms.edit',
    'forms.publish',
    // Agent permissions
    'agents.create',
    'agents.edit',
    'agents.test',
    // Workflow permissions
    'workflows.execute',
    'workflows.monitor',
    // Security permissions
    'security.view_logs',
    // Content permissions
    'content.view',
    'content.create',
    'content.edit',
    // Analytics permissions
    'analytics.view',
    'analytics.export',
    // Integration permissions
    'integrations.view',
    'integrations.configure',
    // MCP permissions
    'mcp.view',
    'mcp.monitor',
    'mcp.analytics',
  ],
  [ROLES.USER]: [
    'dashboard',
    'forms',
    'rag-dashboard',
    'help',
    // Basic form permissions
    'forms.create',
    'forms.edit',
    // Basic agent permissions
    'agents.test',
    // Basic workflow permissions
    'workflows.execute',
    // Content permissions
    'content.view',
    'content.create',
    // Analytics permissions
    'analytics.view',
  ],
  [ROLES.VIEWER]: [
    'dashboard',
    'help',
    // View-only permissions
    'content.view',
    'analytics.view',
    'forms.view',
    'agents.view',
  ],
};

// Get user role from localStorage or context
const getUserRole = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.role || ROLES.USER; // Default to USER role
  } catch {
    return ROLES.USER;
  }
};

// Check if user has permission for a specific route
export const hasPermission = (permission, userRole = null) => {
  const role = userRole || getUserRole();
  const permissions = ROLE_PERMISSIONS[role] || [];

  // Super admin has all permissions
  if (permissions.includes('*')) return true;

  // Check if user has specific permission
  return permissions.includes(permission);
};

// RoleGuard component to protect routes
function RoleGuard({ children, requiredPermission, fallbackPath = '/dashboard' }) {
  const navigate = useNavigate();
  const userRole = getUserRole();
  const hasAccess = hasPermission(requiredPermission, userRole);

  if (!hasAccess) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
      >
        <Card sx={{ maxWidth: 500, textAlign: 'center' }}>
          <CardContent sx={{ p: 4 }}>
            <SecurityIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Access Denied
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              You don't have permission to access this page. Your current role is
              {' '}
              <strong>{userRole}</strong>
              .
            </Typography>
            <Alert severity="warning" sx={{ mb: 3 }}>
              Required permission:
              {' '}
              <strong>{requiredPermission}</strong>
            </Alert>
            <Button
              variant="contained"
              onClick={() => navigate(fallbackPath)}
              sx={{ mr: 2 }}
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
            >
              Go Back
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return children;
}

export default RoleGuard;
